import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AuthContext } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";

import InputPro from "../components/ui/InputPro";
import PasswordInputPro from "../components/ui/PasswordInput";
import Button from "../components/ui/Button";

export default function Login() {
  const { login } = useContext(AuthContext);
  const { t, isRTL } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  /* =========================
      HANDLE CHANGE
  ========================= */
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /* =========================
      SUBMIT
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    const res = await login(form);
    if (res?.status === 200) {
      navigate("/redirect");
    } else {
      let msg = t("auth_login.invalid_credentials");

      switch (res?.status) {
        case 400:
          msg = t("auth_login.invalid_credentials");
          break;
        case 401:
          msg = t("auth_login.errors.unauthorized") || "Unauthorized";
          break;
        case 403:
          msg =
            t("auth_login.errors.forbidden") ||
            "Your account is disabled.";
          break;
        case 404:
          msg = t("auth_login.errors.not_found") || "Account not found.";
          break;
        case 429:
          msg =
            t("auth_login.errors.too_many_requests") ||
            "Too many attempts.";
          break;
        case 500:
          msg = t("auth_login.errors.server") || "Server error.";
          break;
        default:
          msg = t("auth_login.errors.unknown") || "Unknown error.";
      }

      setErrorMsg(msg);
    }

    setLoading(false);
  };

  return (
    <div
      className="
        relative min-h-screen overflow-hidden
        bg-[radial-gradient(circle_at_top_left,_#dff7ee,_transparent_32%),linear-gradient(135deg,_#f8fafc_0%,_#eef2ff_42%,_#f7fee7_100%)]
        px-4 py-8 text-gray-950
        dark:bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_34%),linear-gradient(135deg,_#020617_0%,_#111827_48%,_#052e2b_100%)]
        dark:text-white
      "
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="pointer-events-none absolute -left-24 top-16 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl dark:bg-emerald-500/10" />
      <div className="pointer-events-none absolute -right-20 bottom-10 h-80 w-80 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-500/10" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-200/30 blur-3xl dark:bg-rose-500/10" />

      <main className="relative z-10 mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[2rem] border border-white/60 bg-white/70 shadow-2xl shadow-gray-900/10 backdrop-blur-xl dark:border-white/10 dark:bg-white/10 dark:shadow-black/30 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="hidden min-h-[640px] flex-col justify-between bg-gray-950 p-10 text-white dark:bg-black/30 lg:flex">
            <div>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-lg font-black text-gray-950 shadow-lg">
                T
              </div>
              <div className="mt-8 max-w-md">
                <p className="text-sm font-medium uppercase tracking-[0.22em] text-emerald-300">
                  Teqa Connect
                </p>
                <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight">
                  Manage your commerce workspace with clarity.
                </h1>
                <p className="mt-5 text-base leading-7 text-gray-300">
                  Sign in to continue to your dashboard, monitor activity, and keep every workflow moving from one focused place.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-2xl shadow-black/20 backdrop-blur">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <p className="text-sm font-medium text-white">Dashboard preview</p>
                  <p className="mt-1 text-xs text-gray-400">Live operations overview</p>
                </div>
                <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-medium text-emerald-200">
                  Online
                </span>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                {["Orders", "Stores", "Agents"].map((label, index) => (
                  <div
                    key={label}
                    className="rounded-xl border border-white/10 bg-white/[0.07] p-4"
                  >
                    <div
                      className={`mb-4 h-2 rounded-full ${
                        index === 0
                          ? "w-10 bg-emerald-300"
                          : index === 1
                          ? "w-8 bg-cyan-300"
                          : "w-12 bg-rose-300"
                      }`}
                    />
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="mt-1 text-2xl font-semibold">
                      {index === 0 ? "248" : index === 1 ? "36" : "19"}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-5 space-y-3">
                {["w-8/12", "w-6/12", "w-10/12"].map((barWidth, index) => (
                  <div key={barWidth} className="flex items-center gap-3">
                    <span
                      className={`h-8 w-8 rounded-lg ${
                        index === 0
                          ? "bg-emerald-300/20"
                          : index === 1
                          ? "bg-cyan-300/20"
                          : "bg-rose-300/20"
                      }`}
                    />
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
                      <div className={`h-full rounded-full bg-white/70 ${barWidth}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="flex items-center justify-center px-5 py-8 sm:px-8 lg:px-12">
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-md"
            >
              <div className={isRTL ? "text-right" : "text-left"}>
                <div className="mb-8 flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gray-950 text-base font-black text-white shadow-lg shadow-gray-900/20 dark:bg-white dark:text-gray-950">
                    T
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-950 dark:text-white">
                      Teqa Connect
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Secure workspace access
                    </p>
                  </div>
                </div>

                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  Welcome back
                </p>
                <h2 className="mt-2 text-3xl font-semibold tracking-tight text-gray-950 dark:text-white sm:text-4xl">
                  {t("auth_login.login_title")}
                </h2>
              </div>

              {errorMsg && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="
                    mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3
                    text-sm font-medium text-red-700 shadow-sm
                    dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200
                  "
                >
                  {errorMsg}
                </div>
              )}

              <div className="mt-7 space-y-4">
                <InputPro
                  label={t("auth_login.email")}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                />

                <PasswordInputPro
                  label={t("auth_login.password")}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  showStrength={false}
                />
              </div>

              <div
                className={`mt-5 flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between ${
                  isRTL ? "sm:flex-row-reverse" : ""
                }`}
              >
                <label className="inline-flex cursor-pointer items-center gap-2 text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="
                      h-4 w-4 rounded border-gray-300 text-gray-950
                      focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                      dark:border-gray-600 dark:bg-gray-900 dark:focus:ring-offset-gray-950
                    "
                  />
                  <span>Remember me</span>
                </label>

                <Link
                  to="/reset-password"
                  className="font-medium text-gray-950 underline-offset-4 transition hover:text-emerald-700 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:text-white dark:hover:text-emerald-300 dark:focus:ring-offset-gray-950"
                >
                  {t("auth_login.forgot_password")}
                </Link>
              </div>

              <Button
                loading={loading}
                className="
                  mt-7 h-12 rounded-xl bg-gray-950 text-sm font-semibold
                  shadow-lg shadow-gray-900/20 hover:-translate-y-0.5 hover:bg-gray-800
                  focus:ring-emerald-500 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100
                "
                type="submit"
              >
                {t("auth_login.login_btn")}
              </Button>

              <div
                className={`mt-7 text-center text-sm text-gray-600 dark:text-gray-300 ${
                  isRTL ? "sm:text-right" : "sm:text-left"
                }`}
              >
                <span>New to Teqa Connect? </span>
                <Link
                  to="/register"
                  className="font-semibold text-gray-950 underline-offset-4 transition hover:text-emerald-700 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:text-white dark:hover:text-emerald-300 dark:focus:ring-offset-gray-950"
                >
                  {t("auth_login.create_account")}
                </Link>
              </div>
            </form>
          </section>
        </div>
      </main>
    </div>
  );
}
